import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetSocialVerificationsStatus } from '../api/getSocialVerificationsStatus';
import { profileStorageKeys } from '../const/profileStorageKeys';
import { useUserStore } from '../store/userStoreProvider';
import type { Platform } from '../types/platforms';
type VerificationData = {
  platform: string;
  celebrityId: number;
  profileUrl: string;
  code: string;
};
export function useVerificationPolling() {
  const user = useUserStore(state => state.user);
  const [isVerificationPending, setIsVerificationPending] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(profileStorageKeys.verificationPending) === 'true';
  });
  const [previousVerifications, setPreviousVerifications] = useState<Array<{
    platform: Platform;
    status: string;
  }> | undefined>(undefined);
  const {
    data: socialVerificationsStatuses,
    refetch
  } = useGetSocialVerificationsStatus({
    refetchInterval: undefined
  });
  const hasPendingVerifications = useMemo(() => socialVerificationsStatuses?.some(v => v.status === 'pending' || v.status === 'in_progress') || false, [socialVerificationsStatuses]);

  // Проверяем, что все верификации завершены (success или failed)
  const allVerificationsCompleted = useMemo(() => {
    if (!socialVerificationsStatuses || socialVerificationsStatuses.length === 0) {
      return false;
    }
    // Все верификации должны иметь финальные статусы (не pending и не in_progress)
    return socialVerificationsStatuses.every(v_0 => v_0.status === 'success' || v_0.status === 'failed' || v_0.status === 'expired');
  }, [socialVerificationsStatuses]);

  // Поллинг продолжается если:
  // 1. isVerificationPending = true (пользователь явно запустил верификацию через startPolling)
  // 2. ИЛИ есть активные верификации в API (pending/in_progress)
  // Останавливаем поллинг только когда:
  // - Нет флага isVerificationPending И все верификации в API завершены
  const shouldPoll = useMemo(() => {
    // Если пользователь явно запустил верификацию, поллим независимо от статусов в API
    // (новая верификация может еще не появиться в статусах)
    if (isVerificationPending) {
      return true;
    }
    // Если есть активные верификации в API, поллим
    if (hasPendingVerifications) {
      return true;
    }
    // Если все верификации завершены и нет явного запроса, не поллим
    return false;
  }, [hasPendingVerifications, isVerificationPending]);
  useEffect(() => {
    if (!shouldPoll || !user) return;
    const intervalId = setInterval(() => {
      refetch();
    }, 10000);
    return () => clearInterval(intervalId);
  }, [shouldPoll, user, refetch]);
  const [confirmedSocials, setConfirmedSocials] = useState<Platform[]>([]);
  const [verifiedSocials, setVerifiedSocials] = useState<Platform[]>([]);
  const stableVerifadeSocialsKey = useMemo(() => JSON.stringify(socialVerificationsStatuses), [socialVerificationsStatuses]);
  useEffect(() => {
    if (socialVerificationsStatuses) {
      const verifiedSocials_0 = socialVerificationsStatuses.filter(v_1 => v_1.status === 'success');
      setVerifiedSocials(verifiedSocials_0.map(v_2 => v_2.platform));
    }
  }, [stableVerifadeSocialsKey]);
  const shownModalsRef = useRef<Set<Platform>>(new Set());

  /**
   * Получить список pending верификаций из localStorage
   */
  const getPendingVerifications = useCallback((): VerificationData[] => {
    if (typeof window === 'undefined') return [];
    const data = localStorage.getItem(profileStorageKeys.socialVerifications);
    if (!data) return [];
    try {
      const parsed = JSON.parse(data);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch (error) {
      console.error('Failed to parse verification data:', error);
      return [];
    }
  }, []);

  /**
   * Сохранить список pending верификаций в localStorage
   */
  const savePendingVerifications = useCallback((verifications: VerificationData[]) => {
    if (verifications.length === 0) {
      localStorage.removeItem(profileStorageKeys.socialVerifications);
      localStorage.removeItem(profileStorageKeys.verificationPending);
      setIsVerificationPending(false);
    } else {
      localStorage.setItem(profileStorageKeys.socialVerifications, JSON.stringify(verifications));
      localStorage.setItem(profileStorageKeys.verificationPending, 'true');
      setIsVerificationPending(true);
    }
  }, []);
  const currentVerifications = useMemo(() => {
    if (!socialVerificationsStatuses) return [];
    return socialVerificationsStatuses.map(v_3 => ({
      platform: v_3.platform,
      status: v_3.status
    }));
  }, [socialVerificationsStatuses]);
  useEffect(() => {
    if (!socialVerificationsStatuses || currentVerifications.length === 0) {
      return;
    }
    const pendingVerifications = getPendingVerifications();
    const trackedPlatforms = new Set(pendingVerifications.map(v_4 => v_4.platform));
    const newlyVerifiedPlatforms: Platform[] = [];
    if (previousVerifications === undefined) {
      pendingVerifications.forEach(pendingData => {
        const verification = socialVerificationsStatuses.find(v_5 => v_5.platform === pendingData.platform);
        if (verification && verification.status === 'success' && !shownModalsRef.current.has(pendingData.platform as Platform)) {
          newlyVerifiedPlatforms.push(pendingData.platform as Platform);
        }
      });
      currentVerifications.forEach(current => {
        if (current.status === 'success' && !shownModalsRef.current.has(current.platform) && !newlyVerifiedPlatforms.includes(current.platform)) {
          const wasInProcess = trackedPlatforms.has(current.platform);
          if (wasInProcess) {
            newlyVerifiedPlatforms.push(current.platform);
          }
        }
      });
      setPreviousVerifications(currentVerifications);
    } else {
      pendingVerifications.forEach(pendingData_0 => {
        const verification_0 = socialVerificationsStatuses.find(v_6 => v_6.platform === pendingData_0.platform);
        if (verification_0 && verification_0.status === 'success' && !shownModalsRef.current.has(pendingData_0.platform as Platform) && !newlyVerifiedPlatforms.includes(pendingData_0.platform as Platform)) {
          newlyVerifiedPlatforms.push(pendingData_0.platform as Platform);
        }
      });
      currentVerifications.forEach(current_0 => {
        const previous = previousVerifications.find(p => p.platform === current_0.platform);
        if (current_0.status === 'success' && previous && previous.status !== 'success' && (previous.status === 'pending' || previous.status === 'in_progress')) {
          if (!newlyVerifiedPlatforms.includes(current_0.platform)) {
            newlyVerifiedPlatforms.push(current_0.platform);
          }
        }
        if (trackedPlatforms.has(current_0.platform) && current_0.status === 'success' && !shownModalsRef.current.has(current_0.platform) && !newlyVerifiedPlatforms.includes(current_0.platform)) {
          newlyVerifiedPlatforms.push(current_0.platform);
        }
      });
      if (pendingVerifications.length === 0) {
        currentVerifications.forEach(current_1 => {
          const previous_0 = previousVerifications.find(p_0 => p_0.platform === current_1.platform);
          if (current_1.status === 'success' && previous_0 && (previous_0.status === 'pending' || previous_0.status === 'in_progress')) {
            if (!newlyVerifiedPlatforms.includes(current_1.platform)) {
              newlyVerifiedPlatforms.push(current_1.platform);
            }
          }
        });
      }
      currentVerifications.forEach(current_2 => {
        if (current_2.status === 'success' && !shownModalsRef.current.has(current_2.platform) && !newlyVerifiedPlatforms.includes(current_2.platform)) {
          const previous_1 = previousVerifications.find(p_1 => p_1.platform === current_2.platform);
          if (previous_1 && (previous_1.status === 'pending' || previous_1.status === 'in_progress')) {
            newlyVerifiedPlatforms.push(current_2.platform);
          } else if (trackedPlatforms.has(current_2.platform)) {
            newlyVerifiedPlatforms.push(current_2.platform);
          }
        }
      });
    }
    if (newlyVerifiedPlatforms.length > 0) {
      const uniqueNewPlatforms = newlyVerifiedPlatforms.filter(platform => !shownModalsRef.current.has(platform));
      if (uniqueNewPlatforms.length > 0) {
        uniqueNewPlatforms.forEach(platform_0 => shownModalsRef.current.add(platform_0));
        setConfirmedSocials(prev => {
          const platformsToAdd = uniqueNewPlatforms.filter(platform_1 => !prev.includes(platform_1));
          return [...prev, ...platformsToAdd];
        });
      }
    }
    if (previousVerifications !== undefined) {
      setPreviousVerifications(currentVerifications);
    }
    if (pendingVerifications.length > 0) {
      const stillPendingVerifications: VerificationData[] = [];
      pendingVerifications.forEach(pendingData_1 => {
        const verification_1 = socialVerificationsStatuses.find(v_7 => v_7.platform === pendingData_1.platform);
        if (verification_1 && (verification_1.status === 'pending' || verification_1.status === 'in_progress')) {
          stillPendingVerifications.push(pendingData_1);
        } else if (verification_1 && (verification_1.status === 'success' || verification_1.status === 'failed')) {
          if (verification_1.status === 'success' && !shownModalsRef.current.has(pendingData_1.platform as Platform)) {
            stillPendingVerifications.push(pendingData_1);
          }
        }
      });
      savePendingVerifications(stillPendingVerifications);
    } else if (allVerificationsCompleted && isVerificationPending) {
      // Если нет pending верификаций в localStorage, но все верификации завершены,
      // очищаем флаг isVerificationPending
      setIsVerificationPending(false);
      localStorage.removeItem(profileStorageKeys.verificationPending);
    }
  }, [currentVerifications, previousVerifications, getPendingVerifications, savePendingVerifications, socialVerificationsStatuses, allVerificationsCompleted, isVerificationPending]);
  const startPolling = useCallback((verificationData: VerificationData) => {
    const currentVerifications_0 = getPendingVerifications();
    const existingIndex = currentVerifications_0.findIndex(v_8 => v_8.platform === verificationData.platform);
    if (existingIndex !== -1) {
      currentVerifications_0[existingIndex] = verificationData;
    } else {
      currentVerifications_0.push(verificationData);
    }
    savePendingVerifications(currentVerifications_0);

    // Сразу делаем refetch чтобы получить актуальные статусы верификации
    refetch();
    if (socialVerificationsStatuses) {
      const verification_2 = socialVerificationsStatuses.find(v_9 => v_9.platform === verificationData.platform);
      if (verification_2 && verification_2.status === 'success') {
        setConfirmedSocials(prev_0 => {
          if (!prev_0.includes(verificationData.platform as Platform) && !shownModalsRef.current.has(verificationData.platform as Platform)) {
            shownModalsRef.current.add(verificationData.platform as Platform);
            return [...prev_0, verificationData.platform as Platform];
          }
          return prev_0;
        });
      }
    }
  }, [getPendingVerifications, savePendingVerifications, socialVerificationsStatuses, refetch]);

  /**
   * Остановить polling верификации для конкретной платформы
   * @param platform - платформа для остановки (опционально, если не указана - останавливаем все)
   */
  const stopPolling = useCallback((platform_2?: string) => {
    if (!platform_2) {
      localStorage.removeItem(profileStorageKeys.verificationPending);
      localStorage.removeItem(profileStorageKeys.socialVerifications);
      setIsVerificationPending(false);
    } else {
      const currentVerifications_1 = getPendingVerifications();
      const updatedVerifications = currentVerifications_1.filter(v_10 => v_10.platform !== platform_2);
      savePendingVerifications(updatedVerifications);
    }
  }, [getPendingVerifications, savePendingVerifications]);

  /**
   * Закрыть модалку успешной верификации
   */
  const clearConfirmedSocials = useCallback(() => {
    setConfirmedSocials([]);
  }, []);
  return {
    /** Флаг активного polling (для refetchInterval) */
    isVerificationPending,
    /** Список подтвержденных соц.сетей (для модалки успеха) */
    confirmedSocials,
    /** Список успешно верифицированных соц.сетей */
    verifiedSocials,
    /** Запустить polling верификации */
    startPolling,
    /** Остановить polling верификации */
    stopPolling,
    /** Закрыть модалку успеха */
    clearConfirmedSocials
  };
}